import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Search as SearchIcon, X, History, GitCompare, RotateCcw,
  Download, Eye, Check, ChevronRight, Loader2,
} from "lucide-react";
import { useAuth, canAccess } from "@/lib/auth";

/**
 * Fase 17 — Cronologia versioni menu.
 * Timeline read-only con azioni: Visualizza · Confronta · Ripristina · Esporta.
 * NON modifica flussi di publish/snapshot/ordini/API pubbliche.
 */

function normalise(s) { return (s || "").toString().toLowerCase(); }
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return String(iso).slice(0, 16).replace("T", " "); }
}

const TAG_STYLE = {
  "Attuale":   "bg-emerald-100 text-emerald-800 border-emerald-300",
  "Bootstrap": "bg-slate-100 text-slate-700 border-slate-300",
  "Storica":   "bg-amber-50 text-amber-800 border-amber-300",
};

// ---------------------------------------------------------------------------
// Diff renderer — reused for both "Confronta" and "Anteprima rollback"
// ---------------------------------------------------------------------------
function DiffSummary({ diff }) {
  if (!diff) return null;
  const c = diff.counts || {};
  const g = diff.groups || {};
  const wines = g.wines || {};
  const chip = (label, n, tone = "slate") => n > 0 && (
    <span className={`text-xs rounded-full px-2 py-0.5 border
      ${tone === "green" ? "bg-emerald-50 text-emerald-800 border-emerald-200"
      : tone === "red" ? "bg-rose-50 text-rose-800 border-rose-200"
      : tone === "amber" ? "bg-amber-50 text-amber-800 border-amber-200"
      : "bg-slate-50 text-slate-700 border-slate-200"}`}>
      {label}: <b>{n}</b>
    </span>
  );
  return (
    <div className="space-y-3" data-testid="diff-summary">
      <div className="flex flex-wrap gap-1.5">
        {chip("Nuovi prodotti", c.new_products, "green")}
        {chip("Modificati", c.modified_products, "amber")}
        {chip("Rimossi", c.removed_products, "red")}
        {chip("Esauriti", c.became_unavailable, "red")}
        {chip("Tornati disponibili", c.became_available, "green")}
        {chip("Categorie nuove", c.new_categories, "green")}
        {chip("Categorie nascoste", c.hidden_categories)}
        {chip("Categorie riattivate", c.reactivated_categories, "green")}
        {chip("Reparti spenti", c.disabled_departments)}
        {chip("Reparti riattivati", c.reenabled_departments, "green")}
        {chip("Vini nuovi", c.new_wines, "green")}
        {chip("Vini modificati", c.modified_wines, "amber")}
      </div>
      {c.total === 0 && (
        <div className="text-sm text-ink-500 italic">Nessuna modifica rilevata.</div>
      )}
      {/* Compact per-group lists — limit to first 6 items to keep it scan-able */}
      {c.total > 0 && (
        <div className="grid md:grid-cols-2 gap-3">
          {[
            ["Nuovi prodotti", g.new_products, "green"],
            ["Prodotti modificati", g.modified_products, "amber"],
            ["Prodotti rimossi", g.removed_products, "red"],
            ["Esauriti", g.became_unavailable, "red"],
            ["Tornati disponibili", g.became_available, "green"],
            ["Vini nuovi", wines.new, "green"],
            ["Vini modificati", wines.modified, "amber"],
          ].filter(([, arr]) => (arr || []).length > 0).map(([label, arr, tone]) => (
            <div key={label} className={`rounded-xl p-3 border
              ${tone === "green" ? "bg-emerald-50 border-emerald-200"
              : tone === "red" ? "bg-rose-50 border-rose-200"
              : tone === "amber" ? "bg-amber-50 border-amber-200"
              : "bg-slate-50 border-slate-200"}`}>
              <div className="font-medium text-sm">{label} · {arr.length}</div>
              <ul className="mt-1 space-y-0.5 text-xs">
                {arr.slice(0, 6).map((it, i) => (
                  <li key={i} className="truncate">
                    · {it.name}
                    {it.category_name && <span className="text-ink-500"> ({it.category_name})</span>}
                  </li>
                ))}
                {arr.length > 6 && (
                  <li className="text-ink-500 italic">…e altri {arr.length - 6}</li>
                )}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Compare dialog — pick two versions and diff them
// ---------------------------------------------------------------------------
function CompareDialog({ versions, initialFrom, initialTo, trigger }) {
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(initialFrom || "");
  const [to, setTo]     = useState(initialTo || "");
  const [diff, setDiff] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setFrom(initialFrom || (versions[1]?.id || ""));
      setTo(initialTo || (versions[0]?.id || ""));
      setDiff(null);
    }
  }, [open, initialFrom, initialTo, versions]);

  const run = useCallback(async () => {
    if (!from || !to) return;
    setBusy(true);
    try {
      const r = await api.get(`/admin/menu/diff-versions?from_version=${from}&to_version=${to}`);
      setDiff(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }, [from, to]);

  useEffect(() => { if (open && from && to) run(); }, [open, from, to, run]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Confronta versioni</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-xs text-ink-500 mb-1">Da (base)</div>
            <select value={from} onChange={(e) => setFrom(e.target.value)}
                    data-testid="cmp-from" className="w-full rounded border border-sand-300 px-2 py-1.5 bg-white">
              {versions.map((v) => (
                <option key={v.id} value={v.id}>v.{v.version} · {fmtDate(v.published_at)}</option>
              ))}
            </select>
          </div>
          <div>
            <div className="text-xs text-ink-500 mb-1">A (nuova)</div>
            <select value={to} onChange={(e) => setTo(e.target.value)}
                    data-testid="cmp-to" className="w-full rounded border border-sand-300 px-2 py-1.5 bg-white">
              {versions.map((v) => (
                <option key={v.id} value={v.id}>v.{v.version} · {fmtDate(v.published_at)}</option>
              ))}
            </select>
          </div>
        </div>
        {busy ? (
          <div className="p-6 flex items-center justify-center text-ink-500">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Calcolo differenze…
          </div>
        ) : diff ? (
          <DiffSummary diff={diff} />
        ) : (
          <div className="text-sm text-ink-500">Seleziona due versioni per confrontarle.</div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Rollback dialog with preview counts
// ---------------------------------------------------------------------------
function RollbackDialog({ version, onRestored, trigger }) {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  useEffect(() => {
    if (!open) { setPreview(null); setConfirmText(""); return; }
    let cancelled = false;
    api.get(`/admin/menu/rollback-preview/${version.id}`)
      .then((r) => { if (!cancelled) setPreview(r.data); })
      .catch((e) => toast.error(formatApiError(e)));
    return () => { cancelled = true; };
  }, [open, version.id]);

  const confirmOk = confirmText.trim() === `v.${version.version}`;

  const doRestore = async () => {
    if (!confirmOk) return;
    setBusy(true);
    try {
      await api.post(`/admin/menu/rollback/${version.id}`);
      toast.success(`Draft ripristinato alla v.${version.version}. Clicca Pubblica per renderlo visibile.`);
      setOpen(false);
      onRestored?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const total = preview?.counts?.total ?? null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Ripristina alla v.{version.version}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-ink-500">
          Il ripristino sovrascrive il Draft attuale con lo stato della versione
          selezionata. <b>Non pubblica automaticamente</b>: potrai verificare
          tutto e cliccare Pubblica quando sei pronto.
        </p>
        {preview === null ? (
          <div className="p-4 text-ink-500 flex items-center justify-center">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Anteprima…
          </div>
        ) : total === 0 ? (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-emerald-900 text-sm"
               data-testid="rollback-noop">
            Il Draft è già identico a questa versione. Nessuna modifica verrà applicata.
          </div>
        ) : (
          <DiffSummary diff={preview} />
        )}
        <div className="mt-2">
          <div className="text-xs text-ink-500 mb-1">
            Per confermare digita <b>v.{version.version}</b>
          </div>
          <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                 placeholder={`v.${version.version}`}
                 data-testid="rollback-confirm-input" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
          <Button onClick={doRestore} disabled={!confirmOk || busy}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="rollback-confirm-btn">
            {busy ? "Ripristino…" : <><RotateCcw className="w-4 h-4 mr-1" /> Ripristina Draft</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function AdminMenuHistory() {
  const nav = useNavigate();
  const { user } = useAuth();
  const canRestore = canAccess(user, "admin");
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tagFilter, setTagFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/menu/versions?limit=200");
      setVersions(r.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const nq = normalise(q).trim();
    return versions.filter((v) => {
      if (tagFilter && !(v.tags || []).includes(tagFilter)) return false;
      if (!nq) return true;
      return String(v.version).includes(nq)
          || normalise(v.published_by).includes(nq)
          || normalise(v.notes).includes(nq)
          || normalise(v.published_at).includes(nq);
    });
  }, [versions, q, tagFilter]);

  const doExport = async (v) => {
    try {
      const r = await api.get(`/admin/menu/versions/${v.id}/export`,
                              { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sarabi-menu-v${v.version}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Esportata v.${v.version}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="history-page">
      <div className="flex items-center gap-2 flex-wrap">
        <Link to="/admin/menu" className="btn-outline text-xs py-1 px-3" data-testid="history-back">
          <ArrowLeft className="w-3 h-3 mr-1" /> Menu
        </Link>
        <div>
          <h1 className="font-serif text-2xl flex items-center gap-2">
            <History className="w-6 h-6" /> Cronologia versioni
          </h1>
          <p className="text-xs text-ink-500">
            Ogni Publish crea una versione immutabile. Nessuna viene mai eliminata.
          </p>
        </div>
        <div className="ml-auto flex gap-2">
          {versions.length >= 2 && (
            <CompareDialog versions={versions}
              trigger={
                <Button variant="outline" size="sm" data-testid="cmp-btn">
                  <GitCompare className="w-4 h-4 mr-1" /> Confronta versioni
                </Button>
              } />
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Cerca per versione, utente, nota…" className="pl-9 pr-8"
                 data-testid="history-search" />
          {q && <button onClick={() => setQ("")}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-400"
                        aria-label="Pulisci" data-testid="history-search-clear">
                  <X className="w-4 h-4" />
                </button>}
        </div>
        <div className="flex items-center gap-1">
          {["", "Attuale", "Storica", "Bootstrap"].map((t) => (
            <button key={t || "all"} onClick={() => setTagFilter(t)}
                    data-testid={`history-tag-${t || "all"}`}
                    className={`text-xs rounded-full border px-3 py-1
                                ${tagFilter === t ? "bg-ink-900 text-white border-ink-900"
                                                  : "bg-white border-ink-200 hover:bg-ink-50"}`}>
              {t || "Tutte"}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="p-6 text-ink-500" data-testid="history-loading">Caricamento…</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-sand-300 p-8 text-center text-sm text-ink-500"
             data-testid="history-empty">
          Nessuna versione trovata con i filtri correnti.
        </div>
      ) : (
        <ol className="relative border-l border-sand-300 ml-3 space-y-3">
          {filtered.map((v) => (
            <li key={v.id} data-testid={`history-row-${v.version}`}
                className="ml-4 rounded-2xl border border-sand-300 bg-white p-3 relative">
              <span className={`absolute -left-[22px] top-3 w-3 h-3 rounded-full border-2 border-white
                ${v.is_current ? "bg-emerald-500" : "bg-amber-500"}`} />
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-serif text-lg">v.{v.version}</span>
                {(v.tags || []).map((t) => (
                  <span key={t} className={`text-[10px] uppercase tracking-wider rounded-full border px-2 py-0.5 ${TAG_STYLE[t] || TAG_STYLE.Storica}`}
                        data-testid={`tag-${v.version}-${t.toLowerCase()}`}>
                    {t}
                  </span>
                ))}
                <span className="text-xs text-ink-500">{fmtDate(v.published_at)}</span>
                {v.published_by && <span className="text-xs text-ink-500">· {v.published_by}</span>}
              </div>
              {v.notes && (
                <div className="mt-1 text-sm italic text-ink-700">"{v.notes}"</div>
              )}
              <div className="mt-1.5 text-xs text-ink-500 flex flex-wrap gap-2">
                <span>{v.counts?.categories || 0} categorie</span>
                <span>· {v.counts?.products || 0} prodotti</span>
                <span>· {v.counts?.modifiers || 0} modifier</span>
                {v.counts?.menus > 0 && <span>· {v.counts.menus} menu</span>}
                {v.checksum && <span className="font-mono text-[10px] text-ink-400 ml-auto">{v.checksum.slice(0, 12)}…</span>}
              </div>
              <div className="mt-2 flex items-center gap-1 flex-wrap">
                {versions.length >= 2 && (
                  <CompareDialog versions={versions} initialTo={v.id}
                    initialFrom={versions.find((x) => x.version !== v.version)?.id}
                    trigger={
                      <Button size="sm" variant="outline" data-testid={`cmp-${v.version}-btn`}>
                        <GitCompare className="w-3 h-3 mr-1" /> Confronta
                      </Button>
                    } />
                )}
                <Button size="sm" variant="outline" onClick={() => doExport(v)}
                        data-testid={`export-${v.version}-btn`}>
                  <Download className="w-3 h-3 mr-1" /> Esporta JSON
                </Button>
                {canRestore && !v.is_current && (
                  <RollbackDialog version={v} onRestored={load}
                    trigger={
                      <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white"
                              data-testid={`restore-${v.version}-btn`}>
                        <RotateCcw className="w-3 h-3 mr-1" /> Ripristina
                      </Button>
                    } />
                )}
                {v.is_current && (
                  <span className="text-xs text-emerald-700 flex items-center gap-1 ml-1"
                        data-testid={`current-${v.version}`}>
                    <Check className="w-3 h-3" /> Attualmente pubblicata
                  </span>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
